import ExcelJS from 'exceljs';
import { COUNTRIES } from '@/config/countries';
import { TIMEZONES } from '@/config/timezones';

// Static lookup values
const LOOKUPS = {
  criticality: ['Very High', 'High', 'Medium', 'Low', 'Very Low'],
  domain: ['IT', 'OT', 'Cloud', 'DMZ', 'External', 'IoT', 'Physical'],
  purdueLevel: ['L0', 'L1', 'L2', 'L3', 'L3.5', 'L4', 'L5'],
  provider: ['AWS', 'Azure', 'GCP', 'Oracle', 'IBM', 'Other'],
  environment: ['Production', 'Staging', 'Development', 'Testing'],
  boolean: ['TRUE', 'FALSE'],
  zoneParentType: ['site', 'cloud_service'],
  endpointCategory: [
    'Network Infrastructure',
    'Servers & Compute', 
    'Storage Appliances',
    'Industrial Controllers',
    'HMI & Supervision',
    'Field Devices',
    'Infrastructure'
  ],
  // Use capitalized values to match named range prefixes (e.g., "Site" → "SiteNamesList")
  internetParentType: ['Site', 'Zone', 'System'],
  dmzParentType: ['Site', 'Zone', 'System', 'CloudService'],
  connectionType: ['Fiber', 'DSL', 'Cable', '4G/5G', 'Satellite', 'Leased Line', 'MPLS', 'Other'],
  firewallChain: ['INPUT', 'FORWARD', 'OUTPUT'],
  firewallAction: ['ALLOW', 'DENY', 'DROP'],
  protocol: ['TCP', 'UDP', 'ICMP', 'ANY'],
  // Dynamic lookups from config files
  countryCodes: COUNTRIES.map(c => c.code),
  timezones: TIMEZONES.map(t => t.value),
};

// Sheet configurations with hidden columns and validation
interface SheetColumnConfig {
  key: string;
  label: string;
  width: number;
  hidden?: boolean;
  required?: boolean;
  description?: string;
  dropdown?: string[] | 'dynamic' | 'dependent'; // 'dependent' for INDIRECT formula-based dropdowns
  dynamicRef?: string; // Reference to another sheet's data
  dependsOnColumn?: string; // For dependent dropdowns - column key this depends on
}

const SHEET_CONFIGS: Record<string, { name: string; columns: SheetColumnConfig[]; example: Record<string, string> }> = {
  sites: {
    name: 'Sites',
    columns: [
      { key: 'name', label: 'Site Name', width: 30, required: true, description: 'Unique name for the site' },
      { key: 'site_code', label: 'Site Code', width: 15, description: 'Short identifier code (e.g., STH-01)' },
      { key: 'address', label: 'Address', width: 40, description: 'Physical address' },
      { key: 'country', label: 'Country Code', width: 12, dropdown: 'dynamic', dynamicRef: 'CountryCodes', description: '2-letter country code (e.g., SE, US, DE)' },
      { key: 'timezone', label: 'Timezone', width: 25, dropdown: 'dynamic', dynamicRef: 'Timezones', description: 'IANA timezone (e.g., Europe/Stockholm)' },
      { key: 'criticality', label: 'Criticality', width: 12, dropdown: LOOKUPS.criticality },
      { key: 'owner', label: 'Owner Email', width: 30, dropdown: 'dynamic', dynamicRef: 'Owners', description: 'Owner email address (existing org member or new user to invite)' },
      { key: 'notes', label: 'Notes', width: 40 },
      { key: 'id', label: 'ID (auto-generated)', width: 38, hidden: true, description: 'Leave empty - auto-generated on import' },
    ],
    example: { 
      name: 'Stockholm HQ', 
      site_code: 'STH-01', 
      address: 'Kungsgatan 1, Stockholm', 
      country: 'SE', 
      timezone: 'Europe/Stockholm', 
      criticality: 'High', 
      owner: 'john.doe@company.com' 
    },
  },
  cloud_services: {
    name: 'Cloud Services',
    columns: [
      { key: 'name', label: 'Service Name', width: 30, required: true, description: 'Unique name for the cloud service' },
      { key: 'provider', label: 'Provider', width: 12, required: true, dropdown: LOOKUPS.provider },
      { key: 'account_id', label: 'Account ID', width: 20, description: 'AWS Account ID' },
      { key: 'subscription_id', label: 'Subscription ID', width: 38, description: 'Azure Subscription ID' },
      { key: 'project_id', label: 'Project ID', width: 30, description: 'GCP Project ID' },
      { key: 'region', label: 'Region', width: 20, description: 'Cloud region (e.g., eu-north-1)' },
      { key: 'environment', label: 'Environment', width: 15, dropdown: LOOKUPS.environment },
      { key: 'criticality', label: 'Criticality', width: 12, dropdown: LOOKUPS.criticality },
      { key: 'notes', label: 'Notes', width: 40 },
      { key: 'id', label: 'ID (auto-generated)', width: 38, hidden: true },
    ],
    example: { 
      name: 'Production AWS', 
      provider: 'AWS', 
      account_id: '123456789012', 
      region: 'eu-north-1', 
      environment: 'Production', 
      criticality: 'High' 
    },
  },
  zones: {
    name: 'Zones',
    columns: [
      { key: 'name', label: 'Zone Name', width: 30, required: true, description: 'Unique name for the zone' },
      { key: 'parent_site', label: 'Parent Site', width: 30, required: true, dropdown: 'dynamic', dynamicRef: 'Sites', description: 'Select parent site from dropdown' },
      { key: 'domain', label: 'Domain', width: 12, required: true, dropdown: LOOKUPS.domain },
      { key: 'purdue_level', label: 'Purdue Level', width: 12, dropdown: LOOKUPS.purdueLevel },
      { key: 'cidrs', label: 'CIDRs', width: 30, description: 'Network ranges, semicolon-separated (e.g., 10.0.0.0/24;10.0.1.0/24)' },
      { key: 'vlan_id', label: 'VLAN ID', width: 12, description: 'VLAN identifier (1-4094)' },
      { key: 'data_classification', label: 'Data Classification', width: 20 },
      { key: 'backup_frequency', label: 'Backup Frequency', width: 20 },
      { key: 'criticality', label: 'Criticality', width: 12, dropdown: LOOKUPS.criticality },
      { key: 'notes', label: 'Notes', width: 40 },
      { key: 'id', label: 'ID (auto-generated)', width: 38, hidden: true },
    ],
    example: { 
      name: 'Production OT', 
      parent_site: 'Stockholm HQ', 
      domain: 'OT', 
      purdue_level: 'L2', 
      cidrs: '10.0.10.0/24;10.0.11.0/24',
      vlan_id: '100',
      criticality: 'High' 
    },
  },
  systems: {
    name: 'Systems',
    columns: [
      { key: 'name', label: 'System Name', width: 30, required: true, description: 'Unique name for the system' },
      { key: 'parent_zone', label: 'Parent Zone', width: 30, required: true, dropdown: 'dynamic', dynamicRef: 'Zones', description: 'Select zone from dropdown' },
      { key: 'function', label: 'Function', width: 20, description: 'System function (SCADA, AD, MES, etc.)' },
      { key: 'cidr', label: 'CIDR', width: 18, description: 'Network range (e.g., 10.0.10.0/24)' },
      { key: 'vlan_id', label: 'VLAN ID', width: 12, description: 'VLAN identifier (1-4094)' },
      { key: 'default_gateway', label: 'Default Gateway', width: 15, description: 'Gateway IP (e.g., 10.0.10.1)' },
      { key: 'protocols', label: 'Protocols', width: 30, description: 'Semicolon-separated (e.g., Modbus;OPC UA)' },
      { key: 'rto', label: 'RTO (hours)', width: 12, description: 'Recovery Time Objective in hours' },
      { key: 'rpo', label: 'RPO (hours)', width: 12, description: 'Recovery Point Objective in hours' },
      { key: 'failover_tested', label: 'Failover Tested', width: 15, dropdown: LOOKUPS.boolean },
      { key: 'processes_pii', label: 'Processes PII', width: 15, dropdown: LOOKUPS.boolean },
      { key: 'pii_types', label: 'PII Types', width: 30, description: 'Semicolon-separated list' },
      { key: 'dpia_required', label: 'DPIA Required', width: 15, dropdown: LOOKUPS.boolean },
      { key: 'owner', label: 'Owner Email', width: 30, dropdown: 'dynamic', dynamicRef: 'Owners', description: 'Owner email address' },
      { key: 'notes', label: 'Notes', width: 40 },
      { key: 'id', label: 'ID (auto-generated)', width: 38, hidden: true },
      { key: 'parent_id', label: 'Parent ID', width: 38, hidden: true },
    ],
    example: { 
      name: 'SCADA System', 
      parent_zone: 'Production OT', 
      function: 'SCADA', 
      cidr: '10.0.10.0/24',
      vlan_id: '100',
      default_gateway: '10.0.10.1',
      protocols: 'Modbus;OPC UA', 
      rto: '4', 
      rpo: '1', 
      failover_tested: 'TRUE' 
    },
  },
  endpoints: {
    name: 'Endpoints',
    columns: [
      { key: 'name', label: 'Endpoint Name', width: 30, required: true, description: 'Unique name for the endpoint' },
      { key: 'parent_system', label: 'Parent System', width: 30, dropdown: 'dynamic', dynamicRef: 'Systems', description: 'Select system from dropdown (optional)' },
      { key: 'asset_type', label: 'Asset Type', width: 20, description: 'PLC, HMI, Server, Switch, etc.' },
      { key: 'category', label: 'Category', width: 20, dropdown: LOOKUPS.endpointCategory },
      { key: 'vendor', label: 'Vendor', width: 20 },
      { key: 'model', label: 'Model', width: 25 },
      { key: 'firmware', label: 'Firmware', width: 20 },
      { key: 'ip', label: 'IP Address', width: 15, description: 'IPv4 or IPv6 (e.g., 10.0.10.50)' },
      { key: 'mac', label: 'MAC Address', width: 18, description: 'Format: AA:BB:CC:DD:EE:FF or AA-BB-CC-DD-EE-FF' },
      { key: 'vlan_id', label: 'VLAN ID', width: 12, description: 'VLAN identifier (1-4094)' },
      { key: 'subnet_mask', label: 'Subnet Mask', width: 15, description: 'e.g., 255.255.255.0' },
      { key: 'default_gateway', label: 'Default Gateway', width: 15, description: 'Gateway IP (e.g., 10.0.10.1)' },
      { key: 'serial', label: 'Serial Number', width: 25 },
      { key: 'os', label: 'Operating System', width: 20 },
      { key: 'backup', label: 'Has Backup', width: 12, dropdown: LOOKUPS.boolean },
      { key: 'last_patch', label: 'Last Patch Date', width: 15, description: 'YYYY-MM-DD format' },
      { key: 'eol_date', label: 'EOL Date', width: 15, description: 'YYYY-MM-DD format' },
      { key: 'criticality', label: 'Criticality', width: 12, dropdown: LOOKUPS.criticality },
      { key: 'notes', label: 'Notes', width: 40 },
      { key: 'id', label: 'ID (auto-generated)', width: 38, hidden: true },
      { key: 'parent_id', label: 'Parent ID', width: 38, hidden: true },
    ],
    example: { 
      name: 'PLC-001', 
      parent_system: 'SCADA System', 
      asset_type: 'PLC', 
      category: 'Industrial Controllers', 
      vendor: 'Siemens', 
      model: 'S7-1500', 
      ip: '10.0.10.50',
      mac: 'AA:BB:CC:DD:EE:FF',
      vlan_id: '100',
      subnet_mask: '255.255.255.0',
      default_gateway: '10.0.10.1',
      criticality: 'High' 
    },
  },
  internet_connections: {
    name: 'Internet Connections',
    columns: [
      { key: 'label', label: 'Label', width: 25, required: true, description: 'Connection identifier (e.g., Primary WAN, Backup 4G)' },
      { key: 'parent_type', label: 'Parent Type', width: 15, required: true, dropdown: LOOKUPS.internetParentType, description: 'Site, Zone, or System' },
      { key: 'parent_name', label: 'Parent Name', width: 30, required: true, dropdown: 'dependent', dependsOnColumn: 'parent_type', description: 'Name of parent (changes based on Parent Type)' },
      { key: 'connection_type', label: 'Connection Type', width: 15, dropdown: LOOKUPS.connectionType },
      { key: 'supplier', label: 'Supplier/ISP', width: 25, description: 'Internet service provider' },
      { key: 'description', label: 'Description', width: 40 },
      { key: 'external_link', label: 'External Link', width: 30, description: 'URL to documentation' },
      { key: 'id', label: 'ID (auto-generated)', width: 38, hidden: true },
    ],
    example: {
      label: 'Primary WAN',
      parent_type: 'Site',
      parent_name: 'Stockholm HQ',
      connection_type: 'Fiber',
      supplier: 'Telia',
      description: '1 Gbps fiber connection',
    },
  },
  dmz_configs: {
    name: 'DMZ Configurations',
    columns: [
      { key: 'label', label: 'Label', width: 25, required: true, description: 'DMZ identifier (e.g., External DMZ, Cloud DMZ)' },
      { key: 'parent_type', label: 'Parent Type', width: 15, required: true, dropdown: LOOKUPS.dmzParentType, description: 'Site, Zone, System, or CloudService' },
      { key: 'parent_name', label: 'Parent Name', width: 30, required: true, dropdown: 'dependent', dependsOnColumn: 'parent_type', description: 'Name of parent (changes based on Parent Type)' },
      { key: 'description', label: 'Description', width: 50 },
      { key: 'id', label: 'ID (auto-generated)', width: 38, hidden: true },
    ],
    example: {
      label: 'External DMZ',
      parent_type: 'Site',
      parent_name: 'Stockholm HQ',
      description: 'Perimeter firewall for external access',
    },
  },
  firewall_rules: {
    name: 'Firewall Rules',
    columns: [
      { key: 'dmz_name', label: 'DMZ Name', width: 25, required: true, dropdown: 'dynamic', dynamicRef: 'DMZConfigs', description: 'Name of DMZ configuration' },
      { key: 'rule_order', label: 'Rule Order', width: 12, description: 'Order of rule evaluation (lower = first)' },
      { key: 'chain', label: 'Chain', width: 12, required: true, dropdown: LOOKUPS.firewallChain },
      { key: 'source', label: 'Source', width: 20, description: 'Source IP/CIDR or "any"' },
      { key: 'destination', label: 'Destination', width: 20, description: 'Destination IP/CIDR or "any"' },
      { key: 'port', label: 'Port', width: 15, description: 'Port number or range (e.g., 443, 8080-8090)' },
      { key: 'protocol', label: 'Protocol', width: 12, dropdown: LOOKUPS.protocol },
      { key: 'action', label: 'Action', width: 12, required: true, dropdown: LOOKUPS.firewallAction },
      { key: 'notes', label: 'Notes', width: 40 },
      { key: 'id', label: 'ID (auto-generated)', width: 38, hidden: true },
    ],
    example: {
      dmz_name: 'External DMZ',
      rule_order: '10',
      chain: 'INPUT',
      source: '0.0.0.0/0',
      destination: '10.0.1.0/24',
      port: '443',
      protocol: 'TCP',
      action: 'ALLOW',
      notes: 'Allow HTTPS from internet',
    },
  },
};

// Theme colors
const COLORS = {
  headerBg: 'FF1A1F2C', // Dark background
  headerText: 'FFFFFFFF', // White text
  requiredBg: 'FFFFF3CD', // Light yellow for required
  exampleBg: 'FFE3F2FD', // Light blue for example row
  exampleText: 'FF1976D2', // Blue text
  instructionHeader: 'FF2D3748', // Dark gray
  warningBg: 'FFFEF3C7', // Warning yellow
  dataTableHeader: 'FF4F46E5', // Indigo for data tables
};

export interface OrgMember {
  email: string;
  name?: string | null;
}

export async function generateExcelTemplate(orgMembers?: OrgMember[]): Promise<Blob> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'ComplyManager';
  workbook.created = new Date();

  // 1. Create Instructions sheet
  createInstructionsSheet(workbook);

  // 2. Create Data Tables sheet (visible, for dropdown references and user configuration)
  createDataTablesSheet(workbook, orgMembers || []);

  // 3. Create data sheets
  for (const [key, config] of Object.entries(SHEET_CONFIGS)) {
    createDataSheet(workbook, key, config);
  }

  // Generate blob
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

function createInstructionsSheet(workbook: ExcelJS.Workbook) {
  const sheet = workbook.addWorksheet('Instructions', {
    properties: { tabColor: { argb: 'FF4CAF50' } },
  });

  const instructions = [
    ['🚀 Asset Import Template - Quick Start Guide'],
    [''],
    ['📋 How to Use This Template:'],
    [''],
    ['1️⃣  Start with Sites or Cloud Services sheets - these are your top-level containers'],
    ['2️⃣  Add Zones under Sites or Cloud Services using the dropdown to select parent'],
    ['3️⃣  Add Systems under Zones using the dropdown to select parent zone'],
    ['4️⃣  Add Endpoints under Systems using the dropdown to select parent system'],
    [''],
    ['💡 Tips:'],
    ['• All dropdown fields have a small arrow - click to see available options'],
    ['• Required fields are highlighted in yellow'],
    ['• Use semicolons (;) to separate multiple values (tags, protocols, CIDRs)'],
    ['• Leave ID columns empty - they are auto-generated on import'],
    ['• Boolean fields use TRUE/FALSE (case-insensitive)'],
    ['• Date fields use YYYY-MM-DD format'],
    [''],
    ['🔗 Parent References:'],
    ['• Zones → Select parent site from "Parent Site" dropdown'],
    ['• Systems → Select parent zone from "Parent Zone" dropdown'],
    ['• Endpoints → Select parent system from "Parent System" dropdown (optional)'],
    [''],
    ['👥 Owner Assignment:'],
    ['• Use email addresses in Owner columns'],
    ['• Existing org members will be auto-matched'],
    ['• New email addresses will queue invitations for review after import'],
    [''],
    ['📊 Data Tables Sheet:'],
    ['• View all valid dropdown values in the "Data Tables" sheet'],
    ['• Contains Country Codes, Timezones, and Organization Members'],
    ['• You can add new owners to invite during import'],
    [''],
    ['📝 Example Formats (for reference only):'],
    [''],
    ['Sites:'],
    ['  Name: "Stockholm HQ", Code: "STH-01", Country: "SE", Timezone: "Europe/Stockholm", Criticality: "High"'],
    [''],
    ['Cloud Services:'],
    ['  Name: "Production AWS", Provider: "AWS", Account ID: "123456789012", Region: "eu-north-1"'],
    [''],
    ['Zones:'],
    ['  Name: "Production OT", Parent Site: "Stockholm HQ", Domain: "OT", Purdue Level: "L2", CIDRs: "10.0.10.0/24;10.0.11.0/24"'],
    [''],
    ['Systems:'],
    ['  Name: "SCADA System", Parent Zone: "Production OT", Function: "SCADA", Protocols: "Modbus;OPC UA", RTO: "4", RPO: "1"'],
    [''],
    ['Endpoints:'],
    ['  Name: "PLC-001", Parent System: "SCADA System", Type: "PLC", Category: "Industrial Controllers", Vendor: "Siemens", IP: "10.0.10.50"'],
    [''],
    ['❓ Need Help?'],
    ['• Hover over column headers to see descriptions'],
    ['• Parent dropdowns update automatically as you add entries'],
  ];

  instructions.forEach((row, idx) => {
    const excelRow = sheet.addRow(row);
    
    if (idx === 0) {
      excelRow.font = { bold: true, size: 18, color: { argb: COLORS.instructionHeader } };
      excelRow.height = 30;
    } else if (row[0]?.startsWith('📋') || row[0]?.startsWith('💡') || row[0]?.startsWith('🔗') || row[0]?.startsWith('📊') || row[0]?.startsWith('❓') || row[0]?.startsWith('👥') || row[0]?.startsWith('📝')) {
      excelRow.font = { bold: true, size: 14, color: { argb: COLORS.instructionHeader } };
      excelRow.height = 24;
    } else if (row[0]?.match(/^[1-5]️⃣/)) {
      excelRow.font = { size: 12 };
    } else if (row[0]?.startsWith('Sites:') || row[0]?.startsWith('Cloud Services:') || row[0]?.startsWith('Zones:') || row[0]?.startsWith('Systems:') || row[0]?.startsWith('Endpoints:')) {
      excelRow.font = { bold: true, size: 11, color: { argb: 'FF2196F3' } };
    } else if (row[0]?.startsWith('  Name:')) {
      excelRow.font = { italic: true, size: 10, color: { argb: 'FF666666' } };
    }
  });

  sheet.getColumn(1).width = 100;
}

function createDataTablesSheet(workbook: ExcelJS.Workbook, orgMembers: OrgMember[]) {
  const sheet = workbook.addWorksheet('Data Tables', {
    properties: { tabColor: { argb: 'FF6366F1' } }, // Indigo color
  });

  // Define all lookup columns with their data
  const lookupColumns: { name: string; displayName: string; values: string[]; description: string; isEditable?: boolean }[] = [
    { name: 'Criticality', displayName: 'Criticality', values: LOOKUPS.criticality, description: 'Asset criticality level' },
    { name: 'Domain', displayName: 'Domain', values: LOOKUPS.domain, description: 'Network domain classification' },
    { name: 'PurdueLevel', displayName: 'Purdue Level', values: LOOKUPS.purdueLevel, description: 'ICS/OT Purdue model level' },
    { name: 'Provider', displayName: 'Cloud Provider', values: LOOKUPS.provider, description: 'Cloud service provider' },
    { name: 'Environment', displayName: 'Environment', values: LOOKUPS.environment, description: 'Deployment environment' },
    { name: 'Boolean', displayName: 'True/False', values: LOOKUPS.boolean, description: 'Boolean values' },
    { name: 'EndpointCategory', displayName: 'Endpoint Category', values: LOOKUPS.endpointCategory, description: 'Endpoint device category' },
    { name: 'CountryCodes', displayName: 'Country Codes', values: COUNTRIES.map(c => `${c.code} - ${c.name}`), description: '2-letter ISO country codes' },
    { name: 'Timezones', displayName: 'Timezones', values: TIMEZONES.map(t => t.value), description: 'IANA timezone identifiers' },
    { name: 'Owners', displayName: '👥 Organization Members', values: orgMembers.map(m => m.email), description: '✏️ ADD NEW EMAILS HERE to invite after import!', isEditable: true },
  ];

  // Add instruction row for editable Owners column
  const instructionRow = sheet.addRow(['', '', '', '', '', '', '', '', '', '👆 You can ADD new email addresses below - they will be invited after import!']);
  instructionRow.height = 24;
  const ownersColIdx = lookupColumns.findIndex(c => c.name === 'Owners') + 1;
  const instructionCell = instructionRow.getCell(ownersColIdx);
  instructionCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFD1FAE5' }, // Light green
  };
  instructionCell.font = { bold: true, size: 10, color: { argb: 'FF059669' } };
  instructionCell.alignment = { horizontal: 'center', vertical: 'middle' };

  // Add header row with styling
  const headerRow = sheet.addRow(lookupColumns.map(col => col.displayName));
  headerRow.height = 32;
  headerRow.eachCell((cell, colNumber) => {
    const col = lookupColumns[colNumber - 1];
    const isOwnerCol = col.isEditable;
    
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: isOwnerCol ? 'FF10B981' : COLORS.dataTableHeader }, // Green for Owners
    };
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: isOwnerCol ? 12 : 11 };
    cell.border = {
      bottom: { style: 'medium', color: { argb: 'FF000000' } },
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    
    // Add description as note
    cell.note = `${col.displayName}\n${col.description}`;
  });

  // Add description row
  const descRow = sheet.addRow(lookupColumns.map(col => col.description));
  descRow.height = 22;
  descRow.eachCell((cell, colNumber) => {
    const col = lookupColumns[colNumber - 1];
    const isOwnerCol = col.isEditable;
    
    cell.font = { italic: true, size: 9, color: { argb: isOwnerCol ? 'FF059669' : 'FF666666' } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: isOwnerCol ? 'FFD1FAE5' : 'FFF3F4F6' }, // Light green for Owners
    };
  });

  // Find the maximum number of values across all columns
  const maxRows = Math.max(...lookupColumns.map(col => col.values.length), 100);

  // Add values for each column
  for (let rowIdx = 0; rowIdx < maxRows; rowIdx++) {
    const rowValues = lookupColumns.map(col => col.values[rowIdx] || '');
    const row = sheet.addRow(rowValues);
    
    // Style each cell - alternate colors and highlight Owners column
    row.eachCell((cell, colNumber) => {
      const col = lookupColumns[colNumber - 1];
      const isOwnerCol = col?.isEditable;
      
      if (isOwnerCol) {
        // Light green background for Owners column
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFECFDF5' }, // Very light green
        };
      } else if (rowIdx % 2 === 0) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF9FAFB' },
        };
      }
    });
  }

  // Set column widths
  lookupColumns.forEach((col, idx) => {
    const maxLength = Math.max(
      col.displayName.length,
      ...col.values.map(v => v.length)
    );
    // Make Owners column wider
    const extraWidth = col.isEditable ? 10 : 0;
    sheet.getColumn(idx + 1).width = Math.min(Math.max(maxLength + 2 + extraWidth, 15), 45);
  });

  // Create named ranges for each lookup column
  // Use fixed range of 100 rows for owner list to allow adding new owners
  // Note: +1 for instruction row at top
  lookupColumns.forEach((col, idx) => {
    const colLetter = getColumnLetter(idx + 1);
    // For Owners column, use fixed 100 row range to allow users to add new emails
    const endRow = col.name === 'Owners' ? 103 : col.values.length + 3; // +3 for instruction, header, and description rows
    workbook.definedNames.add(`'Data Tables'!$${colLetter}$4:$${colLetter}$${endRow}`, `${col.name}List`);
  });

  // Reserve dynamic columns for parent references (Sites, Cloud Services, Zones, Systems, DMZ Configs)
  // These will be populated via formulas in the actual data sheets
  const dynamicStartCol = lookupColumns.length + 1;
  
  // Column for Site Names (dynamic from Sites sheet)
  sheet.getColumn(dynamicStartCol).values = ['SiteNames'];
  workbook.definedNames.add(`'Data Tables'!$${getColumnLetter(dynamicStartCol)}$2:$${getColumnLetter(dynamicStartCol)}$500`, 'SiteNamesList');
  
  // Column for Cloud Service Names
  sheet.getColumn(dynamicStartCol + 1).values = ['CloudServiceNames'];
  workbook.definedNames.add(`'Data Tables'!$${getColumnLetter(dynamicStartCol + 1)}$2:$${getColumnLetter(dynamicStartCol + 1)}$500`, 'CloudServiceNamesList');
  
  // Column for Zone Names
  sheet.getColumn(dynamicStartCol + 2).values = ['ZoneNames'];
  workbook.definedNames.add(`'Data Tables'!$${getColumnLetter(dynamicStartCol + 2)}$2:$${getColumnLetter(dynamicStartCol + 2)}$500`, 'ZoneNamesList');
  
  // Column for System Names
  sheet.getColumn(dynamicStartCol + 3).values = ['SystemNames'];
  workbook.definedNames.add(`'Data Tables'!$${getColumnLetter(dynamicStartCol + 3)}$2:$${getColumnLetter(dynamicStartCol + 3)}$500`, 'SystemNamesList');

  // Column for DMZ Config Names
  sheet.getColumn(dynamicStartCol + 4).values = ['DMZConfigNames'];
  workbook.definedNames.add(`'Data Tables'!$${getColumnLetter(dynamicStartCol + 4)}$2:$${getColumnLetter(dynamicStartCol + 4)}$500`, 'DMZConfigNamesList');

  // Hide the dynamic columns (they're just for formula references)
  for (let i = 0; i < 5; i++) {
    sheet.getColumn(dynamicStartCol + i).hidden = true;
  }
}

function createDataSheet(
  workbook: ExcelJS.Workbook, 
  sheetKey: string, 
  config: { name: string; columns: SheetColumnConfig[]; example: Record<string, string> }
) {
  const sheet = workbook.addWorksheet(config.name, {
    properties: { tabColor: { argb: getSheetColor(sheetKey) } },
    views: [{ state: 'frozen', ySplit: 1 }], // Freeze header row only
  });

  const visibleColumns = config.columns.filter(col => !col.hidden);
  const hiddenColumns = config.columns.filter(col => col.hidden);

  // Add header row
  const headerRow = sheet.addRow(visibleColumns.map(col => col.label));
  headerRow.height = 25;
  headerRow.eachCell((cell, colNumber) => {
    const col = visibleColumns[colNumber - 1];
    
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: col.required ? COLORS.requiredBg : COLORS.headerBg },
    };
    cell.font = { 
      bold: true, 
      color: { argb: col.required ? 'FF856404' : COLORS.headerText },
      size: 11,
    };
    cell.border = {
      bottom: { style: 'medium', color: { argb: 'FF000000' } },
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };

    // Add comment/note with description and example (plain text to avoid Excel corruption)
    let noteText = col.label;
    if (col.description) noteText += '\n' + col.description;
    if (col.required) noteText += '\n⚠️ Required field';
    if (Array.isArray(col.dropdown)) {
      noteText += '\n✓ Values: ' + col.dropdown.join(', ');
    }
    // Add example value from config
    const exampleValue = config.example[col.key];
    if (exampleValue) {
      noteText += '\n📝 Example: ' + exampleValue;
    }
    cell.note = noteText;
  });

  // Set column widths
  visibleColumns.forEach((col, idx) => {
    sheet.getColumn(idx + 1).width = col.width;
  });

  // Add hidden columns at the end (for backward compatibility)
  const hiddenStartCol = visibleColumns.length + 1;
  hiddenColumns.forEach((col, idx) => {
    const colNum = hiddenStartCol + idx;
    sheet.getColumn(colNum).width = col.width;
    sheet.getColumn(colNum).hidden = true;
    sheet.getCell(1, colNum).value = col.label;
    sheet.getCell(1, colNum).font = { color: { argb: 'FF999999' }, italic: true };
  });

  // Add data validations for dropdown columns - start at row 2 (after header only)
  visibleColumns.forEach((col, idx) => {
    const colLetter = getColumnLetter(idx + 1);

    if (col.dropdown === 'dynamic') {
      // Dynamic dropdowns reference Data Tables sheet or other data sheets
      const formula = getDynamicDropdownFormula(col.dynamicRef || '', sheetKey);
      if (formula) {
        // Apply validation to rows 2-100 (after header)
        for (let row = 2; row <= 100; row++) {
          const cell = sheet.getCell(`${colLetter}${row}`);
          cell.dataValidation = {
            type: 'list',
            allowBlank: !col.required,
            formulae: [formula],
            showErrorMessage: true,
            errorTitle: 'Invalid Selection',
            error: `Please select a value from the ${col.dynamicRef} dropdown`,
          };
        }
      }
    } else if (col.dropdown === 'dependent' && col.dependsOnColumn) {
      // Dependent dropdowns use INDIRECT formula based on another column's value
      const dependsOnColIdx = visibleColumns.findIndex(c => c.key === col.dependsOnColumn);
      if (dependsOnColIdx >= 0) {
        const dependsOnColLetter = getColumnLetter(dependsOnColIdx + 1);
        // Create INDIRECT formula: =INDIRECT(A2&"NamesList")
        for (let row = 2; row <= 100; row++) {
          const cell = sheet.getCell(`${colLetter}${row}`);
          cell.dataValidation = {
            type: 'list',
            allowBlank: !col.required,
            formulae: [`INDIRECT(${dependsOnColLetter}${row}&"NamesList")`],
            showErrorMessage: true,
            errorTitle: 'Invalid Selection',
            error: 'Please select a valid parent first, then choose from available options',
          };
        }
      }
    } else if (Array.isArray(col.dropdown)) {
      // Static dropdown - use inline list
      const formulaStr = `"${col.dropdown.join(',')}"`;
      for (let row = 2; row <= 100; row++) {
        const cell = sheet.getCell(`${colLetter}${row}`);
        cell.dataValidation = {
          type: 'list',
          allowBlank: !col.required,
          formulae: [formulaStr],
          showErrorMessage: true,
          errorTitle: 'Invalid Value',
          error: `Please select from: ${col.dropdown.join(', ')}`,
        };
      }
    }
  });

  // Add dynamic name formulas if this is a parent-referenceable sheet
  addDynamicNameFormulas(workbook, sheet, sheetKey);
}

function getDynamicDropdownFormula(ref: string, _currentSheet: string): string | null {
  // Map dynamic references to their named ranges or formulas
  switch (ref) {
    case 'Sites':
      return 'SiteNamesList';
    case 'Cloud Services':
      return 'CloudServiceNamesList';
    case 'Zones':
      return 'ZoneNamesList';
    case 'Systems':
      return 'SystemNamesList';
    case 'DMZConfigs':
      return 'DMZConfigNamesList';
    case 'CountryCodes':
      // Return just the country codes (first 2 chars) from the Data Tables
      return 'CountryCodesList';
    case 'Timezones':
      return 'TimezonesList';
    case 'Owners':
      return 'OwnersList';
    default:
      return null;
  }
}

function addDynamicNameFormulas(workbook: ExcelJS.Workbook, sheet: ExcelJS.Worksheet, sheetKey: string) {
  // Get the Data Tables sheet to add array formulas for dynamic lists
  const dataTablesSheet = workbook.getWorksheet('Data Tables');
  if (!dataTablesSheet) return;

  // Determine which column in Data Tables to populate based on this sheet
  // Dynamic columns start at column 11 (K) in Data Tables
  const dynamicColumns: Record<string, number> = {
    'sites': 11,           // Site names go to column K
    'cloud_services': 12,  // Cloud service names to column L
    'zones': 13,           // Zone names to column M
    'systems': 14,         // System names to column N
    'dmz_configs': 15,     // DMZ config names to column O
  };

  const targetCol = dynamicColumns[sheetKey];
  if (!targetCol) return;

  // Add a formula that will populate the dynamic list from this sheet
  // We'll use a simple reference to the name column (column A)
  // Note: ExcelJS doesn't support true array formulas well, so we'll use individual cell references
  const nameColLetter = 'A';
  
  for (let row = 2; row <= 100; row++) {
    const targetCell = dataTablesSheet.getCell(row, targetCol);
    const sourceRow = row; // Data starts at row 2 in data sheets (after header only)
    targetCell.value = { 
      formula: `IF('${sheet.name}'!${nameColLetter}${sourceRow}="","",'${sheet.name}'!${nameColLetter}${sourceRow})`,
      result: undefined 
    };
  }
}

function getColumnLetter(colNumber: number): string {
  let letter = '';
  while (colNumber > 0) {
    const remainder = (colNumber - 1) % 26;
    letter = String.fromCharCode(65 + remainder) + letter;
    colNumber = Math.floor((colNumber - 1) / 26);
  }
  return letter;
}

function getSheetColor(sheetKey: string): string {
  // Tab colors aligned with legend colors:
  // Sites = neutral gray, Cloud = purple, Zones = blue (IT-leaning), 
  // Systems = orange (OT-leaning), Endpoints = gray, Internet = green, DMZ = yellow, Firewall = red
  const colors: Record<string, string> = {
    sites: 'FF6B7280',           // Gray (neutral container)
    cloud_services: 'FF8B5CF6',  // Purple (Cloud domain)
    zones: 'FF3B82F6',           // Blue (IT-leaning)
    systems: 'FFF97316',         // Orange (OT-leaning)
    endpoints: 'FF9CA3AF',       // Light Gray (varies by domain)
    internet_connections: 'FF10B981', // Green (connectivity)
    dmz_configs: 'FFFBBF24',     // Yellow (DMZ domain)
    firewall_rules: 'FFEF4444',  // Red (security)
  };
  return colors[sheetKey] || 'FF757575';
}

export async function downloadExcelTemplateEnhanced(orgMembers?: OrgMember[]) {
  const blob = await generateExcelTemplate(orgMembers);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `asset_import_template_${new Date().toISOString().split('T')[0]}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ===== EXPORT WITH DATA =====

import type { Site } from '@/types/site';
import type { Zone } from '@/types/zone';
import type { System } from '@/types/system';
import type { Endpoint } from '@/types/endpoint';
import type { CloudService } from '@/types/cloudService';
import type { InternetConnection } from '@/types/internetConnection';
import type { DMZConfig, FirewallRule } from '@/types/dmzConfig';

export interface AssetExportData {
  sites: Site[];
  zones: Zone[];
  systems: System[];
  endpoints: Endpoint[];
  cloudServices: CloudService[];
  internetConnections?: InternetConnection[];
  dmzConfigs?: DMZConfig[];
  firewallRules?: FirewallRule[];
}

export async function generateExcelExportWithData(
  data: AssetExportData,
  orgMembers?: OrgMember[]
): Promise<Blob> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'ComplyManager';
  workbook.created = new Date();

  // 1. Create Instructions sheet (same as template)
  createInstructionsSheet(workbook);

  // 2. Create Data Tables sheet
  createDataTablesSheet(workbook, orgMembers || []);

  // 3. Create data sheets WITH data
  createDataSheetWithData(workbook, 'sites', SHEET_CONFIGS.sites, mapSitesToRows(data.sites));
  createDataSheetWithData(workbook, 'cloud_services', SHEET_CONFIGS.cloud_services, mapCloudServicesToRows(data.cloudServices));
  createDataSheetWithData(workbook, 'zones', SHEET_CONFIGS.zones, mapZonesToRows(data.zones, data.sites));
  createDataSheetWithData(workbook, 'systems', SHEET_CONFIGS.systems, mapSystemsToRows(data.systems, data.zones));
  createDataSheetWithData(workbook, 'endpoints', SHEET_CONFIGS.endpoints, mapEndpointsToRows(data.endpoints, data.systems));
  
  // 4. Create new network entity sheets (always create, even if empty, for consistency with template)
  createDataSheetWithData(workbook, 'internet_connections', SHEET_CONFIGS.internet_connections, 
    mapInternetConnectionsToRows(data.internetConnections || [], data.sites, data.zones, data.systems));
  createDataSheetWithData(workbook, 'dmz_configs', SHEET_CONFIGS.dmz_configs,
    mapDMZConfigsToRows(data.dmzConfigs || [], data.sites, data.zones, data.systems, data.cloudServices));
  createDataSheetWithData(workbook, 'firewall_rules', SHEET_CONFIGS.firewall_rules,
    mapFirewallRulesToRows(data.firewallRules || [], data.dmzConfigs || []));

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

function createDataSheetWithData(
  workbook: ExcelJS.Workbook,
  sheetKey: string,
  config: { name: string; columns: SheetColumnConfig[]; example: Record<string, string> },
  dataRows: Record<string, string>[]
) {
  const sheet = workbook.addWorksheet(config.name, {
    properties: { tabColor: { argb: getSheetColor(sheetKey) } },
    views: [{ state: 'frozen', ySplit: 1 }], // Freeze header row only
  });

  const visibleColumns = config.columns.filter(col => !col.hidden);
  const hiddenColumns = config.columns.filter(col => col.hidden);

  // Add header row
  const headerRow = sheet.addRow(visibleColumns.map(col => col.label));
  headerRow.height = 25;
  headerRow.eachCell((cell, colNumber) => {
    const col = visibleColumns[colNumber - 1];
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: col.required ? COLORS.requiredBg : COLORS.headerBg },
    };
    cell.font = {
      bold: true,
      color: { argb: col.required ? 'FF856404' : COLORS.headerText },
      size: 11,
    };
    cell.border = {
      bottom: { style: 'medium', color: { argb: 'FF000000' } },
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };

    if (col.description || col.required) {
      let noteText = col.label;
      if (col.description) noteText += '\n' + col.description;
      if (col.required) noteText += '\n⚠️ Required field';
      cell.note = noteText;
    }
  });

  // Add data rows (no example row for exports)
  dataRows.forEach((rowData, rowIdx) => {
    const rowValues = visibleColumns.map(col => rowData[col.key] || '');
    const row = sheet.addRow(rowValues);
    row.height = 20;

    // Alternate row colors
    if (rowIdx % 2 === 1) {
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF9FAFB' },
        };
      });
    }
  });

  // Set column widths
  visibleColumns.forEach((col, idx) => {
    sheet.getColumn(idx + 1).width = col.width;
  });

  // Add hidden columns at the end
  const hiddenStartCol = visibleColumns.length + 1;
  hiddenColumns.forEach((col, idx) => {
    const colNum = hiddenStartCol + idx;
    sheet.getColumn(colNum).width = col.width;
    sheet.getColumn(colNum).hidden = true;
    sheet.getCell(1, colNum).value = col.label;
    sheet.getCell(1, colNum).font = { color: { argb: 'FF999999' }, italic: true };

    // Add hidden column data
    dataRows.forEach((rowData, rowIdx) => {
      sheet.getCell(rowIdx + 2, colNum).value = rowData[col.key] || '';
    });
  });

  // Add data validations for dropdown columns
  const startRow = 2; // Data starts at row 2 (no example row)
  const endRow = Math.max(dataRows.length + 10, 100); // Extra rows for adding new data

  visibleColumns.forEach((col, idx) => {
    const colLetter = getColumnLetter(idx + 1);

    if (col.dropdown === 'dynamic') {
      const formula = getDynamicDropdownFormula(col.dynamicRef || '', sheetKey);
      if (formula) {
        for (let row = startRow; row <= endRow; row++) {
          const cell = sheet.getCell(`${colLetter}${row}`);
          cell.dataValidation = {
            type: 'list',
            allowBlank: !col.required,
            formulae: [formula],
            showErrorMessage: true,
            errorTitle: 'Invalid Selection',
            error: `Please select a value from the ${col.dynamicRef} dropdown`,
          };
        }
      }
    } else if (col.dropdown === 'dependent' && col.dependsOnColumn) {
      // Dependent dropdowns use INDIRECT formula based on another column's value
      const dependsOnColIdx = visibleColumns.findIndex(c => c.key === col.dependsOnColumn);
      if (dependsOnColIdx >= 0) {
        const dependsOnColLetter = getColumnLetter(dependsOnColIdx + 1);
        for (let row = startRow; row <= endRow; row++) {
          const cell = sheet.getCell(`${colLetter}${row}`);
          cell.dataValidation = {
            type: 'list',
            allowBlank: !col.required,
            formulae: [`INDIRECT(${dependsOnColLetter}${row}&"NamesList")`],
            showErrorMessage: true,
            errorTitle: 'Invalid Selection',
            error: 'Please select a valid parent first, then choose from available options',
          };
        }
      }
    } else if (Array.isArray(col.dropdown)) {
      const formulaStr = `"${col.dropdown.join(',')}"`;
      for (let row = startRow; row <= endRow; row++) {
        const cell = sheet.getCell(`${colLetter}${row}`);
        cell.dataValidation = {
          type: 'list',
          allowBlank: !col.required,
          formulae: [formulaStr],
          showErrorMessage: true,
          errorTitle: 'Invalid Value',
          error: `Please select from: ${col.dropdown.join(', ')}`,
        };
      }
    }
  });

  // Add dynamic name formulas for parent references
  addDynamicNameFormulas(workbook, sheet, sheetKey);
}

// Data mapping functions
function mapSitesToRows(sites: Site[]): Record<string, string>[] {
  return sites.map(site => ({
    id: site.id,
    name: site.name,
    site_code: site.site_code || '',
    address: site.address || '',
    country: site.country || '',
    timezone: site.timezone || '',
    criticality: site.criticality || '',
    owner: site.owner || '',
    notes: site.notes || '',
  }));
}

function mapCloudServicesToRows(cloudServices: CloudService[]): Record<string, string>[] {
  return cloudServices.map(cs => ({
    id: cs.id,
    name: cs.name,
    provider: cs.provider || '',
    account_id: cs.account_id || '',
    subscription_id: cs.subscription_id || '',
    project_id: cs.project_id || '',
    region: cs.region || '',
    environment: cs.environment || '',
    criticality: cs.criticality || '',
    notes: cs.notes || '',
  }));
}

function mapZonesToRows(zones: Zone[], sites: Site[]): Record<string, string>[] {
  return zones.map(zone => {
    const parentSite = sites.find(s => s.id === zone.site_id);
    return {
      id: zone.id,
      name: zone.name,
      parent_site: parentSite?.name || '',
      domain: zone.domain || '',
      purdue_level: zone.purdue_level || '',
      cidrs: zone.cidrs?.join(';') || '',
      data_classification: zone.data_classification || '',
      backup_frequency: zone.backup_frequency || '',
      criticality: zone.criticality || '',
      notes: zone.description || '',
    };
  });
}

function mapSystemsToRows(systems: System[], zones: Zone[]): Record<string, string>[] {
  return systems.map(system => {
    const parentZone = zones.find(z => z.id === system.zone_id);
    return {
      id: system.id,
      name: system.name,
      parent_zone: parentZone?.name || '',
      function: system.function || '',
      protocols: system.protocols?.join(';') || '',
      rto: system.rto?.toString() || '',
      rpo: system.rpo?.toString() || '',
      failover_tested: system.failover_tested ? 'TRUE' : 'FALSE',
      processes_pii: system.processes_pii ? 'TRUE' : 'FALSE',
      pii_types: system.pii_types?.join(';') || '',
      dpia_required: system.dpia_required ? 'TRUE' : 'FALSE',
      owner: system.owner || '',
      notes: '',
      parent_id: system.zone_id || '',
    };
  });
}

function mapEndpointsToRows(endpoints: Endpoint[], systems: System[]): Record<string, string>[] {
  return endpoints.map(endpoint => {
    const parentSystem = systems.find(s => s.id === endpoint.system_id);
    return {
      id: endpoint.id,
      name: endpoint.name,
      parent_system: parentSystem?.name || '',
      asset_type: endpoint.asset_type || '',
      category: endpoint.category || '',
      vendor: endpoint.vendor || '',
      model: endpoint.model || '',
      firmware: endpoint.firmware || '',
      ip: endpoint.ip || '',
      mac: endpoint.mac || '',
      serial: endpoint.serial || '',
      os: endpoint.os || '',
      backup: endpoint.backup ? 'TRUE' : 'FALSE',
      last_patch: endpoint.last_patch || '',
      eol_date: endpoint.eol_date || '',
      criticality: endpoint.criticality || '',
      notes: endpoint.notes || '',
      parent_id: endpoint.system_id || '',
    };
  });
}

function mapInternetConnectionsToRows(
  connections: InternetConnection[], 
  sites: Site[], 
  zones: Zone[], 
  systems: System[]
): Record<string, string>[] {
  return connections.map(conn => {
    let parentType = '';
    let parentName = '';
    
    if (conn.site_id) {
      parentType = 'Site'; // Use capitalized to match dropdown values
      parentName = sites.find(s => s.id === conn.site_id)?.name || '';
    } else if (conn.zone_id) {
      parentType = 'Zone';
      parentName = zones.find(z => z.id === conn.zone_id)?.name || '';
    } else if (conn.system_id) {
      parentType = 'System';
      parentName = systems.find(s => s.id === conn.system_id)?.name || '';
    }
    
    return {
      id: conn.id,
      label: conn.label,
      parent_type: parentType,
      parent_name: parentName,
      connection_type: conn.connection_type || '',
      supplier: conn.supplier || '',
      description: conn.description || '',
      external_link: conn.external_link || '',
    };
  });
}

function mapDMZConfigsToRows(
  configs: DMZConfig[], 
  sites: Site[], 
  zones: Zone[], 
  systems: System[],
  cloudServices: CloudService[]
): Record<string, string>[] {
  return configs.map(config => {
    let parentType = '';
    let parentName = '';
    
    if (config.site_id) {
      parentType = 'Site'; // Use capitalized to match dropdown values
      parentName = sites.find(s => s.id === config.site_id)?.name || '';
    } else if (config.zone_id) {
      parentType = 'Zone';
      parentName = zones.find(z => z.id === config.zone_id)?.name || '';
    } else if (config.system_id) {
      parentType = 'System';
      parentName = systems.find(s => s.id === config.system_id)?.name || '';
    } else if (config.cloud_service_id) {
      parentType = 'CloudService';
      parentName = cloudServices.find(c => c.id === config.cloud_service_id)?.name || '';
    }
    
    return {
      id: config.id,
      label: config.label,
      parent_type: parentType,
      parent_name: parentName,
      description: config.description || '',
    };
  });
}

function mapFirewallRulesToRows(rules: FirewallRule[], dmzConfigs: DMZConfig[]): Record<string, string>[] {
  return rules.map(rule => {
    const dmz = dmzConfigs.find(d => d.id === rule.dmz_config_id);
    return {
      id: rule.id,
      dmz_name: dmz?.label || '',
      rule_order: rule.rule_order.toString(),
      chain: rule.chain,
      source: rule.source || 'any',
      destination: rule.destination || 'any',
      port: rule.port || '',
      protocol: rule.protocol || 'TCP',
      action: rule.action,
      notes: rule.notes || '',
    };
  });
}
